import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";

import Loader from "../../common/Loader";
import NoDataMessage from "../../common/NoDataMessage";
import {
  PlatformSubscriptionRow,
  PlatformUserDetail,
  PlatformUserTeamMembership,
} from "../../common/platformTypes";
import { Date as DateView } from "../../components/Date";
import { TeamLink } from "../../components/DocumentLink";
import {
  DocumentTable,
  DocumentTableProperty,
} from "../../components/DocumentTable";
import { DocumentTabNav } from "../../components/DocumentTabNav";
import MenuBar from "../../components/MenuBar";
import { PlatformSubscriptionsTable } from "../../components/PlatformSubscriptionsTable";
import {
  platformUserInfo,
  platformUserSubscriptions,
} from "../../services/api/Platform";

type Tab = "details" | "teams" | "subscriptions";

export const PlatformUserPage: React.FunctionComponent<
  RouteComponentProps<{ userId: string }>
> = ({ match }) => {
  const userId = match.params.userId;
  const [tab, setTab] = useState<Tab>("details");
  const [user, setUser] = useState<PlatformUserDetail>();
  const [subscriptions, setSubscriptions] = useState<PlatformSubscriptionRow[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, subs] = await Promise.all([
        platformUserInfo(userId),
        platformUserSubscriptions(userId),
      ]);
      setUser(detail);
      setSubscriptions(subs);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col p-0">
          <MenuBar />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="card mt-2 mb-3">
            <div className="card-header pb-3">
              <h2 className="card-title mb-0">
                {user ? user.name : "Platform User"}
              </h2>
              <small className="text-monospace text-muted">{userId}</small>
            </div>
          </div>

          <DocumentTabNav
            initialTab="details"
            tabs={[
              {
                id: "details",
                label: "Details",
                onClick: () => setTab("details"),
              },
              { id: "teams", label: "Teams", onClick: () => setTab("teams") },
              {
                id: "subscriptions",
                label: "Subscriptions",
                onClick: () => setTab("subscriptions"),
              },
            ]}
          />

          <div className="card bg-dark mt-2">
            {loading ? (
              <Loader />
            ) : error ? (
              <div className="alert alert-danger mb-0">{error}</div>
            ) : !user ? (
              <div className="alert alert-warning mb-0">User not found</div>
            ) : tab === "details" ? (
              <Details user={user} />
            ) : tab === "teams" ? (
              <Teams user={user} />
            ) : (
              <PlatformSubscriptionsTable
                subscriptions={subscriptions}
                showMemberRole
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Details: React.FunctionComponent<{ user: PlatformUserDetail }> = ({
  user,
}) => (
  <table className="table table-dark table-sm table-hover mb-0">
    <tbody>
      <tr>
        <th style={{ width: "18%" }}>Id</th>
        <td className="text-monospace">{user._id}</td>
      </tr>
      <tr>
        <th>Name</th>
        <td>{user.name}</td>
      </tr>
      <tr>
        <th>Email</th>
        <td>{user.email ?? "—"}</td>
      </tr>
      <tr>
        <th>Identities</th>
        <td>{user.identityTypes.join(", ") || "—"}</td>
      </tr>
      <tr>
        <th>Timezone</th>
        <td>{user.timezone ?? "—"}</td>
      </tr>
      <tr>
        <th>Created</th>
        <td>
          <DateView date={new Date(user.createdAt)} />
        </td>
      </tr>
    </tbody>
  </table>
);

/** DocumentTable keys on `_id`; a membership is unique by its team here. */
type UserTeamRow = PlatformUserTeamMembership & { _id: string };

const TEAM_PROPERTIES: DocumentTableProperty<UserTeamRow>[] = [
  {
    title: "Team",
    render: (d) => <TeamLink docId={d.teamId}>{d.teamName}</TeamLink>,
    style: { maxWidth: "40%" },
  },
  {
    title: "Type",
    render: (d) => <span className="text-capitalize">{d.teamType}</span>,
  },
  {
    title: "Role",
    render: (d) => <span className="text-capitalize">{d.role}</span>,
  },
];

const Teams: React.FunctionComponent<{ user: PlatformUserDetail }> = ({
  user,
}) => {
  if (!user.teams.length) {
    return <NoDataMessage componentName="team" />;
  }
  return (
    <DocumentTable<UserTeamRow>
      objects={user.teams.map((t) => ({ ...t, _id: t.teamId }))}
      properties={TEAM_PROPERTIES}
    />
  );
};
